package com.heelion.farmafreelance.ui

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.heelion.farmafreelance.agent.AvvisiFarmy
import com.heelion.farmafreelance.agent.AzioneFarmy
import com.heelion.farmafreelance.agent.CandidatoFarmacista
import com.heelion.farmafreelance.agent.CriteriFarmacista
import com.heelion.farmafreelance.agent.RicercaFarmacisti
import com.heelion.farmafreelance.agent.Province
import com.heelion.farmafreelance.agent.AzioneProposta
import com.heelion.farmafreelance.agent.IntentClassifier
import com.heelion.farmafreelance.agent.InterpreteAzioni
import com.heelion.farmafreelance.models.CreatoreTipo
import com.heelion.farmafreelance.models.Farmacia
import com.heelion.farmafreelance.models.Farmacista
import com.heelion.farmafreelance.models.Match
import com.heelion.farmafreelance.models.MatchStato
import com.heelion.farmafreelance.models.RicercaSalvata
import com.heelion.farmafreelance.models.UserRole
import com.heelion.farmafreelance.models.comeCreatore
import com.heelion.farmafreelance.models.Professionista
import com.heelion.farmafreelance.models.Professione
import com.heelion.farmafreelance.models.isProfessionista
import com.heelion.farmafreelance.voice.VoiceCommandParser
import kotlinx.coroutines.CompletableDeferred
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/** Un pulsante offerto da Farmy sotto la risposta (es. "Proponi a Laura Rossi"): il tocco vale come conferma. */
class SceltaAzione(val etichetta: String, val esegui: suspend () -> EsitoAzione)

/** Cosa dire all'utente dopo un'azione; [chiudi] = chiudere il popup (es. si è aperta un'altra pagina). */
data class EsitoAzione(val testo: String, val chiudi: Boolean = false, val scelte: List<SceltaAzione> = emptyList())

/**
 * Esegue le azioni di Farmy nell'app con le stesse funzioni dei pulsanti (AuthViewModel).
 * Regola di sicurezza: le azioni che cambiano dati condivisi o avvisano un'altra persona
 * (rispondere a una proposta, mandare una proposta) prima si DESCRIVONO e aspettano un "sì";
 * aprire un profilo o gestire i preferiti (reversibile, riguarda solo te) si fanno subito.
 */
class EsecutoreAzioni(
    private val viewModel: AuthViewModel,
    /** Apre la scheda di una farmacia o di un professionista (come toccarla nei risultati di ricerca). */
    private val apriProfilo: (Professionista?, Farmacia?) -> Unit
) {
    private class AzioneInAttesa(val descrizione: String, val esegui: suspend () -> EsitoAzione)

    private var inAttesa by mutableStateOf<AzioneInAttesa?>(null)

    val haAzioneInAttesa: Boolean get() = inAttesa != null

    /** true se [testo] è un sì/no che risponde all'azione in attesa (va passato a [rispondiConferma]). */
    fun eRispostaConferma(testo: String): Boolean =
        inAttesa != null && (IntentClassifier.normalizza(testo) in SI || IntentClassifier.normalizza(testo) in NO)

    /** "sì" esegue l'azione in attesa, "no" la annulla. */
    suspend fun rispondiConferma(testo: String): EsitoAzione {
        val azione = inAttesa ?: return EsitoAzione("Non c'è nessuna azione in attesa.")
        inAttesa = null
        return if (IntentClassifier.normalizza(testo) in SI) azione.esegui() else EsitoAzione("Va bene, non faccio niente.")
    }

    /** Qualsiasi altra frase fa decadere l'azione in attesa: niente conferme "fuori contesto". */
    fun dimenticaAzioneInAttesa() {
        inAttesa = null
    }

    suspend fun prepara(azione: AzioneFarmy): EsitoAzione = when (azione) {
        is AzioneFarmy.RispondiProposta -> preparaRisposta(azione)
        is AzioneFarmy.ProponiTurno -> preparaProposta(azione.nome)
        is AzioneFarmy.ApriProfilo -> apriProfiloDi(azione.nome)
        is AzioneFarmy.Preferito -> preferito(azione.nome, azione.aggiungi)
        is AzioneFarmy.TrovaFarmacista -> trovaFarmacista(azione.testo)
        is AzioneFarmy.SalvaAvviso -> salvaAvviso(azione.testo)
        is AzioneFarmy.PrendiTurno -> preparaPrendiTurno(azione.testo)
    }

    /**
     * "Mi accetti il turno alla Farmacia Rossi di sabato 5 novembre": cerca tra i turni aperti
     * delle controparti (in tutta Italia) quello indicato e, dopo un "sì", lo accetta come il
     * pulsante "Accetta" dei turni urgenti in Home. Poi chi l'ha pubblicato deve confermare.
     */
    private suspend fun preparaPrendiTurno(testo: String): EsitoAzione {
        if (viewModel.currentUserRole == null) return EsitoAzione("Accedi per accettare i turni.")
        val adesso = Clock.System.now()
        val oggi = adesso.toLocalDateTime(TimeZone.currentSystemDefault()).date
        val data = VoiceCommandParser.dataMenzionata(testo, oggi)
        if (!InterpreteAzioni.indicaUnTurno(testo, data)) {
            return EsitoAzione("Quale turno vuoi accettare? Dimmi la farmacia e/o il giorno, per esempio \"accetta il turno della Farmacia Rossi di sabato\".")
        }
        val annunci = viewModel.annunciControparti(null)
            ?: return EsitoAzione("Non riesco a caricare i turni aperti in questo momento: controlla la connessione e riprova.")
        val trovati = InterpreteAzioni.turniDaPrendere(annunci, viewModel.currentMatches, testo, data, oggi, adesso)
        return when {
            trovati.isEmpty() -> EsitoAzione(
                "Non trovo turni aperti" + (data?.let { " per il ${it.dayOfMonth}/${it.monthNumber}" } ?: "") +
                    (InterpreteAzioni.nomeCercato(testo).takeIf { it.isNotBlank() }?.let { " di \"$it\"" } ?: "") +
                    ". Forse è già stato preso, o non è ancora pubblicato. Chiedimi \"ci sono turni nuovi?\" per vedere quelli disponibili."
            )
            trovati.size == 1 -> {
                val a = trovati.single()
                val descrizione = InterpreteAzioni.descriviTurno(a, oggi)
                inAttesa = AzioneInAttesa(descrizione) { prendi(a, oggi) }
                EsitoAzione("Vuoi accettare $descrizione? Rispondi \"sì\" per confermare o \"no\" per lasciar perdere.")
            }
            // Più turni: un pulsante per ciascuno, il tocco vale come conferma.
            else -> EsitoAzione(
                "Ho trovato ${trovati.size} turni: " +
                    trovati.take(3).mapIndexed { i, a -> "${i + 1}) ${InterpreteAzioni.descriviTurno(a, oggi)}" }.joinToString("; ") +
                    ". Tocca quello che vuoi accettare" + (if (trovati.size > 3) ", o dimmi il giorno per restringere." else "."),
                scelte = trovati.take(3).map { a ->
                    val g = a.dataTurno.toLocalDateTime(TimeZone.UTC).date
                    SceltaAzione("Accetta ${g.dayOfMonth}/${g.monthNumber} ${a.nomeCreatore.orEmpty()}".trim()) { prendi(a, oggi) }
                }
            )
        }
    }

    private suspend fun prendi(a: com.heelion.farmafreelance.models.AnnuncioTurno, oggi: kotlinx.datetime.LocalDate): EsitoAzione {
        val risultato = CompletableDeferred<Boolean>()
        viewModel.respondToMatch("smart-${a.id}", "ACCETTA") { risultato.complete(it) }
        val chi = a.nomeCreatore?.takeIf { it.isNotBlank() } ?: when (a.creatoDa) {
            CreatoreTipo.FARMACIA -> "la farmacia"
            CreatoreTipo.INFERMIERE -> "l'infermiere"
            CreatoreTipo.FARMACISTA -> "il farmacista"
        }
        return if (risultato.await()) {
            EsitoAzione("Fatto: hai accettato ${InterpreteAzioni.descriviTurno(a, oggi)}. Ora $chi deve confermare: riceverai una notifica quando lo fa.")
        } else EsitoAzione("Non ci sono riuscito: ${viewModel.actionErrorMessage ?: "riprova dalla Home"}.")
    }

    // --- "Avvisami quando…" (ricerche salvate, il server avvisa) ---
    val avvisi: List<RicercaSalvata> get() = viewModel.ricercheSalvate
    suspend fun ricaricaAvvisi() = viewModel.caricaRicercheSalvate()
    suspend fun cancellaAvviso(id: String) = viewModel.cancellaRicerca(id)

    /** Salvare un avviso non cambia dati di altri ed è reversibile: niente "sì", ma conferma chiara. */
    private suspend fun salvaAvviso(testo: String): EsitoAzione {
        val ruolo = viewModel.currentUserRole ?: return EsitoAzione("Accedi per salvare un avviso.")
        // Serve l'id vero dell'utente (profilo caricato): è quello a cui il server manda le notifiche.
        if (viewModel.currentUserProfessionista?.id == null && viewModel.currentUserFarmacia?.id == null) {
            return EsitoAzione("Sto ancora caricando il tuo profilo, riprova tra qualche secondo.")
        }
        val myId = viewModel.getUnivocoUtenteId() ?: return EsitoAzione("Accedi per salvare un avviso.")
        val ricerca = AvvisiFarmy.ricerca(testo, myId, ruolo, viewModel.zonaUtente) ?: return EsitoAzione("Gli avvisi sono per farmacisti, infermieri e farmacie.")
        return when (viewModel.salvaRicerca(ricerca)) {
            201 -> EsitoAzione(
                "Fatto! Ti avviserò appena escono ${ricerca.descrizione}. " +
                    "Li trovi, e puoi toglierli, in \"Avvisi turni\" qui sotto."
            )
            409 -> EsitoAzione("Hai già 10 avvisi: cancellane uno in \"Avvisi turni\" e riprova.")
            else -> EsitoAzione("Non riesco a salvare l'avviso in questo momento: controlla la connessione e riprova.")
        }
    }

    /**
     * "cerco farmacista galenico a 35 € l'ora di sabato a Bergamo" (o "cerco un infermiere per
     * medicazioni…"): ricerca del server (competenza, zona) + disponibilità e tariffa dagli annunci
     * "cerco lavoro" della stessa professione; i migliori 3 con "Proponi".
     */
    private suspend fun trovaFarmacista(testo: String): EsitoAzione {
        if (viewModel.currentUserRole != UserRole.FARMACIA) {
            return EsitoAzione("Questa ricerca è per le farmacie. Per cercare farmacie dimmi per esempio \"cerca farmacie a Milano\".")
        }
        val oggi = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        val criteri = RicercaFarmacisti.estrai(testo, oggi)
        // Nel database le province sono sigle ("RM"): "in provincia di Roma" / "a Roma" -> "RM". Un comune
        // che è anche capoluogo cerca in tutta la sua provincia (nel profilo si indica la provincia operativa).
        val provincia = criteri.provincia?.let { Province.sigla(it) ?: it }
            ?: criteri.comune?.let { Province.sigla(it) }
            ?: if (criteri.comune == null) viewModel.zonaUtente.orEmpty() else ""
        val comuneDetto = criteri.comune
        val comune = if (comuneDetto != null && Province.sigla(comuneDetto) == null) comuneDetto else ""
        val infermieri = criteri.professione == Professione.INFERMIERE
        val chi = if (infermieri) "infermieri" else "farmacisti"
        val farmacisti: List<Professionista> = if (infermieri) {
            viewModel.searchInfermieri(comune = comune, competenza = criteri.competenza.orEmpty(), provincia = provincia)
        } else {
            viewModel.searchFarmacisti(comune = comune, competenza = criteri.competenza.orEmpty(), provincia = provincia)
        }
        val annunci = viewModel.getAnnunciControparte(provincia.ifBlank { null }, criteri.professione)
        val preferiti = viewModel.currentPreferiti.map { it.targetId }.toSet()
        val candidati = RicercaFarmacisti.ordina(farmacisti, annunci.orEmpty(), criteri, preferiti)
        val filtri = listOfNotNull(
            criteri.competenza?.lowercase(),
            (criteri.comune ?: provincia.ifBlank { null })?.let { "in zona $it" },
            criteri.quando.ifEmpty { null },
            criteri.tariffaMax?.let { "entro ${it.toInt()} €/h" }
        ).joinToString(" ")
        if (candidati.isEmpty()) return EsitoAzione("Non trovo $chi $filtri. Prova ad allargare la zona o togliere un criterio.")
        val migliori = candidati.take(3)
        return EsitoAzione(
            testo = "Ho trovato ${candidati.size} $chi $filtri. I migliori: " +
                migliori.mapIndexed { i, c -> "${i + 1}) ${RicercaFarmacisti.descrivi(c)}" }.joinToString("; ") + "." +
                (if (annunci == null) " (Disponibilità non caricate: controlla la connessione.)" else "") +
                " Tocca \"Proponi\" per contattarne uno.",
            scelte = migliori.map { c -> SceltaAzione("Proponi a ${c.farmacista.nome} ${c.farmacista.cognome}") { proponiA(c, criteri) } }
        )
    }

    /** Con disponibilità pubblicata per quel giorno la si accetta (come nella Home); se no proposta diretta. */
    private suspend fun proponiA(c: CandidatoFarmacista, criteri: CriteriFarmacista): EsitoAzione {
        val nome = "${c.farmacista.nome} ${c.farmacista.cognome}"
        val risultato = CompletableDeferred<Boolean>()
        val disponibilita = c.disponibilita
        if (disponibilita?.id != null) {
            viewModel.respondToMatch("smart-${disponibilita.id}", "ACCETTA") { risultato.complete(it) }
            return if (risultato.await()) EsitoAzione("Fatto: hai accettato la disponibilità di $nome. Ora $nome deve confermare: riceverà una notifica.")
            else EsitoAzione("Non ci sono riuscito: ${viewModel.actionErrorMessage ?: "riprova dalla Home"}.")
        }
        val quando = criteri.quando.takeIf { it.isNotEmpty() }?.let { " $it" }.orEmpty()
        val tariffa = criteri.tariffaMax?.let { " a ${it.toInt()} €/h" }.orEmpty()
        val messaggio = "Ciao, siamo la farmacia ${viewModel.currentUserFarmacia?.nomeFarmacia ?: "Farmacia"}, " +
            "l'ho trovato/a su FarmaFreelance e volevo proporLe un turno$quando$tariffa."
        viewModel.sendDirectProposal(c.farmacista.id ?: return EsitoAzione("Profilo non valido."), criteri.professione.name, messaggio) { risultato.complete(it) }
        return if (risultato.await()) EsitoAzione("Fatto: proposta inviata a $nome. Riceverà una notifica.")
        else EsitoAzione("Non sono riuscito a inviare la proposta, riprova tra poco.")
    }

    private suspend fun preparaRisposta(richiesta: AzioneFarmy.RispondiProposta): EsitoAzione {
        val ruolo = viewModel.currentUserRole ?: return EsitoAzione("Accedi per gestire le proposte.")
        val azione = richiesta.azione ?: return EsitoAzione(
            "Vuoi accettare, rifiutare, confermare o disdire? Dimmi per esempio \"accetta la proposta della Farmacia Rossi\"."
        )
        val oggi = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
        val data = VoiceCommandParser.dataMenzionata(richiesta.testo, oggi)
        val candidati = InterpreteAzioni.candidati(azione, ruolo, viewModel.currentMatches, richiesta.testo, data)
        // "Accetta il turno della Farmacia Rossi di sabato" senza una proposta così: è un turno aperto da prendere.
        if (azione == AzioneProposta.ACCETTA && candidati.isEmpty() && InterpreteAzioni.indicaUnTurno(richiesta.testo, data)) {
            return preparaPrendiTurno(richiesta.testo)
        }
        return when {
            candidati.isEmpty() -> EsitoAzione(
                "Non trovo turni da ${azione.verbo}" +
                    (if (data != null || InterpreteAzioni.nomeCercato(richiesta.testo).isNotBlank()) " con queste indicazioni" else "") +
                    ". Li trovi tutti nella Home."
            )
            candidati.size > 1 -> EsitoAzione(
                "Ho trovato ${candidati.size} turni da ${azione.verbo}: " +
                    candidati.take(3).joinToString("; ") { InterpreteAzioni.descrivi(it) } +
                    ". Dimmi il nome o la data per scegliere."
            )
            else -> {
                val m = candidati.single()
                val descrizione = InterpreteAzioni.descrivi(m)
                inAttesa = AzioneInAttesa(descrizione) { rispondiA(m, azione) }
                EsitoAzione("Vuoi ${azione.verbo} $descrizione? Rispondi \"sì\" per confermare o \"no\" per lasciar perdere.")
            }
        }
    }

    private suspend fun preparaProposta(nome: String): EsitoAzione {
        val ruolo = viewModel.currentUserRole ?: return EsitoAzione("Accedi per mandare proposte.")
        if (nome.isBlank()) return EsitoAzione("A chi vuoi mandare la proposta? Dimmi per esempio \"proponi un turno alla Farmacia Rossi\".")
        val target = trova(nome) ?: return EsitoAzione(nonTrovato(nome))
        target.errore?.let { return EsitoAzione(it) }
        val messaggio = if (ruolo == UserRole.FARMACISTA) {
            "Ciao, sono Dott./Dott.ssa ${viewModel.currentUserFarmacista?.cognome.orEmpty()}, vi ho trovato su FarmaFreelance e volevo proporVi dei turni che posso svolgere."
        } else if (ruolo == UserRole.INFERMIERE) {
            val io = viewModel.currentUserInfermiere?.let { "${it.nome} ${it.cognome}" }.orEmpty()
            "Ciao, sono $io, infermiere/a: vi ho trovato su FarmaFreelance e volevo proporVi dei turni che posso svolgere."
        } else {
            "Ciao, siamo la farmacia ${viewModel.currentUserFarmacia?.nomeFarmacia ?: "Farmacia"}, l'ho trovato/a su FarmaFreelance e volevo proporLe dei turni che possa svolgere."
        }
        inAttesa = AzioneInAttesa(target.nome) {
            val risultato = CompletableDeferred<Boolean>()
            viewModel.sendDirectProposal(target.id, target.ruolo, messaggio) { risultato.complete(it) }
            if (risultato.await()) EsitoAzione("Fatto: proposta inviata a ${target.nome}. Riceverà una notifica.")
            else EsitoAzione("Non sono riuscito a inviare la proposta, riprova tra poco.")
        }
        return EsitoAzione("Vuoi mandare una proposta diretta a ${target.nome}? Messaggio: \"$messaggio\" Rispondi \"sì\" o \"no\".")
    }

    private suspend fun apriProfiloDi(nome: String): EsitoAzione {
        val target = trova(nome) ?: return EsitoAzione(nonTrovato(nome))
        target.errore?.let { return EsitoAzione(it) }
        apriProfilo(target.farmacista, target.farmacia)
        return EsitoAzione("Ecco il profilo di ${target.nome}.", chiudi = true)
    }

    private suspend fun preferito(nome: String, aggiungi: Boolean): EsitoAzione {
        if (nome.isBlank()) return EsitoAzione("Quale farmacia o farmacista? Dimmi per esempio \"aggiungi la Farmacia Rossi ai preferiti\".")
        val target = trova(nome) ?: return EsitoAzione(nonTrovato(nome))
        target.errore?.let { return EsitoAzione(it) }
        val gia = viewModel.isFavorite(target.id)
        if (gia == aggiungi) {
            return EsitoAzione("${target.nome} " + if (aggiungi) "è già tra i tuoi preferiti." else "non è tra i tuoi preferiti.")
        }
        viewModel.togglePreferito(target.id, target.ruolo)
        return EsitoAzione(if (aggiungi) "Fatto: ${target.nome} è tra i tuoi preferiti." else "Fatto: ho tolto ${target.nome} dai preferiti.")
    }

    /** Una farmacia (per i professionisti) o un farmacista/infermiere (per le farmacie) cercati per nome. */
    private class Trovato(
        val id: String, val ruolo: String, val nome: String,
        val farmacista: Professionista? = null, val farmacia: Farmacia? = null, val errore: String? = null
    )

    private suspend fun trova(nome: String): Trovato? {
        val ruolo = viewModel.currentUserRole ?: return null
        val parole = nome.split(" ").filter { it.length >= 3 }
        fun corrisponde(testo: String) = parole.all { IntentClassifier.normalizza(testo).contains(it) }
        val risultati: List<Trovato> = if (ruolo.isProfessionista) {
            viewModel.searchFarmacie(query = nome).filter { corrisponde(it.nomeFarmacia) || corrisponde("${it.nomeFarmacia} ${it.citta.orEmpty()}") }
                .mapNotNull { f -> f.id?.let { Trovato(it, "FARMACIA", listOfNotNull(f.nomeFarmacia, f.citta).joinToString(", "), farmacia = f) } }
        } else {
            // La farmacia cerca tra farmacisti e infermieri.
            viewModel.searchFarmacisti(query = nome).filter { corrisponde("${it.nome} ${it.cognome}") }
                .mapNotNull { f -> f.id?.let { Trovato(it, "FARMACISTA", "${f.nome} ${f.cognome}", farmacista = f) } } +
                viewModel.searchInfermieri(query = nome).filter { corrisponde("${it.nome} ${it.cognome}") }
                    .mapNotNull { f -> f.id?.let { Trovato(it, "INFERMIERE", "${f.nome} ${f.cognome} (infermiere)", farmacista = f) } }
        }
        return when {
            risultati.isEmpty() -> null
            risultati.size == 1 -> risultati.single()
            else -> Trovato(
                "", "", "", errore = "Ho trovato più risultati per \"$nome\": " +
                    risultati.take(3).joinToString("; ") { it.nome } + ". Dimmi il nome completo."
            )
        }
    }

    private fun nonTrovato(nome: String) = if (nome.isBlank()) "Di chi? Dimmi il nome della farmacia, del farmacista o dell'infermiere."
    else "Non trovo nessuno che si chiami \"$nome\"."

    /**
     * "Chiedi a Farmy" da una notifica: Farmy riassume la proposta (chi, quando, orario, compenso,
     * zona, eventuali altri turni confermati quel giorno) e offre i pulsanti delle risposte
     * possibili adesso. Toccare un pulsante vale come conferma (come "Proponi a…").
     */
    suspend fun spiegaProposta(m: Match): EsitoAzione {
        val mio = viewModel.currentUserRole?.comeCreatore ?: return EsitoAzione("Accedi per gestire le proposte.")
        val annuncio = viewModel.getAnnuncioByIdSuspend(m.annuncioId)
        val chi = m.nomeControparte?.takeIf { it.isNotBlank() } ?: "La controparte"
        val giorno = (annuncio?.dataTurno ?: m.dataTurno)?.toLocalDateTime(TimeZone.UTC)?.date
        val quando = buildString {
            giorno?.let { append("del ${it.dayOfMonth}/${it.monthNumber}") }
            annuncio?.let { append(", ${it.oraInizio}-${it.oraFine}") }
            annuncio?.compenso?.let { c -> append(", ${if (c % 1.0 == 0.0) c.toInt().toString() else c.toString().replace('.', ',')} €/h") }
        }
        val citta = m.cittaControparte?.takeIf { it.isNotBlank() }
        val provincia = m.provinciaControparte?.takeIf { it.isNotBlank() }
        val dove = if (citta != null && provincia != null) "$citta ($provincia)" else citta ?: provincia.orEmpty()
        val situazione = when (m.stato) {
            MatchStato.PROPOSTO -> if (m.propostoDa == mio) "Aspetti la sua risposta" else "$chi ti propone il turno"
            MatchStato.ACCETTATO -> if (m.propostoDa == mio) "$chi ha accettato il tuo turno e aspetta la tua conferma" else "Hai accettato: aspetti la conferma di $chi"
            MatchStato.CONFERMATO -> "Il turno con $chi è confermato"
            MatchStato.COMPLETATO -> "Il turno con $chi è già stato svolto"
            MatchStato.RIFIUTATO, MatchStato.ANNULLATO -> "La proposta con $chi è stata annullata"
        }
        // Altri impegni confermati lo stesso giorno: da sapere prima di accettare.
        val altri = viewModel.currentMatches.count {
            it.id != m.id && it.stato == MatchStato.CONFERMATO && giorno != null &&
                it.dataTurno?.toLocalDateTime(TimeZone.UTC)?.date == giorno
        }
        val avviso = if (altri > 0) " Attenzione: quel giorno hai già ${if (altri == 1) "un turno confermato" else "$altri turni confermati"}." else ""
        val azioni = AzioneProposta.possibili(m, mio)
        val testo = "$situazione $quando".trim() + (if (dove.isNotBlank()) ", zona $dove" else "") + "." + avviso +
            if (azioni.isEmpty()) " Non c'è niente da fare." else " Cosa vuoi fare?"
        return EsitoAzione(testo, scelte = azioni.map { a -> SceltaAzione(a.etichetta) { rispondiA(m, a) } })
    }

    /** Esegue la risposta [azione] alla proposta [m], come i pulsanti della Home. */
    suspend fun rispondiA(m: Match, azione: AzioneProposta): EsitoAzione {
        val risultato = CompletableDeferred<Boolean>()
        viewModel.respondToMatch(m.id ?: "smart-${m.annuncioId}", azione.codice) { risultato.complete(it) }
        val descrizione = InterpreteAzioni.descrivi(m)
        return if (risultato.await()) EsitoAzione("Fatto: ${passato(azione)} $descrizione.")
        else EsitoAzione("Non ci sono riuscito: ${viewModel.actionErrorMessage ?: "riprova dalla Home"}.")
    }

    private fun passato(a: AzioneProposta) = when (a) {
        AzioneProposta.ACCETTA -> "hai accettato"
        AzioneProposta.RIFIUTA -> "hai rifiutato"
        AzioneProposta.CONFERMA -> "hai confermato"
        AzioneProposta.RITIRA -> "hai ritirato la tua accettazione per"
        AzioneProposta.DISDICI -> "hai disdetto"
    }

    private companion object {
        val SI = setOf("si", "sì", "ok", "va bene", "conferma", "confermo", "procedi", "certo", "si grazie", "si procedi", "vai")
        val NO = setOf("no", "annulla", "lascia perdere", "non importa", "niente", "no grazie", "stop")
    }
}
